import { useDispatch, useSelector } from 'react-redux'
import { Badge, Row } from 'antd'
import React, { useMemo, useState } from 'react'
import styles from './DataLegend.module.scss'
import stylesBlock from './DataLegendBlock.module.scss'
import { withOverlay } from '../../hocs/withOverlay'
import {
	absAction,
	absPlayed,
	calculateCombos,
	calculateGtoRange,
	convertationActionMultiStack,
	filteredValues,
	getFullName,
	rgbString,
} from '../../helpers/helpers'
import { selectMatrixPreferences, selectReports, selectView } from '../../Redux/selectors/selectors'
import { useComponentLocale } from '../../hooks/useComponentLocale'
import { useCommonTheme, useCurrentTheme } from '../../hooks/useCurrentTheme'
import { useTypeSelector } from '../../hooks/useTypeHooks'
import { constants } from '../../Viewer/viewModeConstants'
import { SecondaryButton } from '../SecondaryButton/SecondaryButton'
import { setStrategyFilters } from '../../Redux/_actions/viewer'
import { shadowVariable } from '../../Viewer/utils'
import classNames from 'classnames'

/**
 * Этот компонент отрисовывает легенду действий, доступных в данный ход.
 * @param sections {array} - содержит объекты данных для легенды
 *        sections.name - имя действия
 *        sections.id - id действия
 *        sections.color - цвет действия
 * @param activeIndexArray {number[]} - содержит индекс активного действия
 * @param isAggFLop {boolean} - для разделения легенды во Viewer и FlopAgg
 * @param onSectionClicked {function} - функция выбора действия
 * @param style - стили обертки легенды
 * @param isViewer {boolean} - показывает откуда приходят Sections
 * @param isUnionMultiReport {boolean} - рендерится ли легенда в отчетах с несколькими стеками или позициями
 * @param sizingsStructureData {array} - массив с сайзингами
 * @param isBlockLayout {boolean} - показывает легенду блоками
 * @param isCircleLayout {boolean} - показывает легенду кружочками
 * @return {ReactNode} - строка с легендой действий*/

export default function DataLegend({
	sections,
	activeIndexArray,
	onSectionClicked = null,
	style,
	isAggFLop,
	sizingsStructureData,
	isViewer,
	isCircleLayout,
	isBlockLayout,
	isUnionMultiReport,
}) {
	const {
		action_struct,
		activeQueryTraceIndex,
		trace,
		graphicsModeElementsVisibility,
		strategyFilters,
		structureWithSameCountOfAction,
		viewMode,
		reportsType,
	} = useSelector(selectView)
	const {
		flopAgg: { averageFlopValue },
	} = useTypeSelector(selectReports)
	const { threshold } = useSelector(selectMatrixPreferences)
	const dispatch = useDispatch()

	const theme = useCurrentTheme()
	const {
		matrix: { shadow },
	} = useCommonTheme()

	const customBorderColor = theme.matrix.substrate['border color']
	const { combosAndDraws } = graphicsModeElementsVisibility

	const { combos: combosFilter, draws: drawsFilter, activeActions, suits } = strategyFilters

	const getLocale = useComponentLocale()
	const combosLocale = getLocale({ component: 'viewer_legend', section: 'combos' })

	const fontColor = theme.matrix.substrate.textcolor1playermatrix
	const textShadow = shadowVariable[shadow]

	const [isHover, setIsHover] = useState({})

	let activeCard = activeQueryTraceIndex === -1 ? trace?.findIndex((card) => card.is_active) : activeQueryTraceIndex
	const checkParentActions = isAggFLop ? !isAggFLop : combosAndDraws
	const isCenterLayout = viewMode === constants.GRAPHICS && reportsType === constants.EQ_GRAPHIC
	const actionLength = isUnionMultiReport ? structureWithSameCountOfAction.length : action_struct?.length

	const renderSections = useMemo(() => {
		const legendData = []
		let abs = 0

		if (isCircleLayout) {
			return sections
		}

		if (isViewer) {
			if (isUnionMultiReport) {
				for (let i = 0; i < structureWithSameCountOfAction.length; i++) {
					let currentAbs = 0
					const length = structureWithSameCountOfAction[i].length

					for (let j = 0; j < length; j++) {
						if (!structureWithSameCountOfAction[i][j]) {
							continue
						}

						let actionsData = {
							...structureWithSameCountOfAction[i][j],
							values: filteredValues(structureWithSameCountOfAction[i][j], combosFilter, drawsFilter, suits, threshold),
						}

						currentAbs = currentAbs + absPlayed(actionsData)
					}

					abs = abs + currentAbs
				}
			} else {
				for (let i = 0; i < sections.length; i++) {
					let currentAction = action_struct?.find((el) => el?.action === sections[i].name)
					currentAction = {
						...currentAction,
						values: filteredValues(currentAction, combosFilter, drawsFilter, suits, threshold),
					}

					abs = abs + absPlayed(currentAction)
				}
			}

			if (isUnionMultiReport) {
				abs = abs / structureWithSameCountOfAction.length
			}
		}

		if (isUnionMultiReport && structureWithSameCountOfAction.length !== 0) {
			let isActive = true
			let actionLegendStyle = isBlockLayout ? stylesBlock.actionLegend : styles.actionLegend
			let combos
			let gtoRange
			const absStacks = []
			const combosStacks = []

			for (let i = 0; i < structureWithSameCountOfAction.length; i++) {
				const length = structureWithSameCountOfAction[i].length
				const absForStack = []
				const combosForStack = []

				for (let j = 0; j < length; j++) {
					if (!structureWithSameCountOfAction[i][j]) {
						absForStack.push(0)
						combosForStack.push(0)

						continue
					}

					let actionsData = {
						...structureWithSameCountOfAction[i][j],
						values: filteredValues(structureWithSameCountOfAction[i][j], combosFilter, drawsFilter, suits, threshold),
					}
					const abs = absAction(actionsData)
					const played = absPlayed(actionsData)
					const combos = calculateCombos(actionsData)

					absForStack.push(isNaN(played / abs) ? 0 : played / abs)
					combosForStack.push(combos)
				}

				absStacks.push(absForStack)
				combosStacks.push(combosForStack)
			}

			gtoRange = absStacks
				.reduce((acc, cur) => acc.map((element, index) => element + cur[index]))
				.map((element) => (element / structureWithSameCountOfAction.length) * 100)

			combos = combosStacks
				.reduce((acc, cur) => acc.map((element, index) => element + cur[index]))
				.map((element) => element / structureWithSameCountOfAction.length)

			for (let i = 0; i < sections.length; i++) {
				const actionName = convertationActionMultiStack(sections[i].name, sections[i].raiseIndex, sections[i].isAllIn)

				if (activeIndexArray && activeIndexArray.length > 0) {
					isActive = activeIndexArray.includes(i)
				}

				legendData.push({
					isActive,
					actionLegendStyle,
					id: sections[i].id,
					color: sections[i].color,
					name: getFullName(actionName),
					gtoRange: gtoRange[i].toFixed(2),
					width: gtoRange[i],
					combos: combos[i].toFixed(2),
				})
			}
		} else {
			for (let i = 0; i < sections.length; i++) {
				let isActive = true
				let actionLegendStyle = isBlockLayout ? stylesBlock.actionLegend : styles.actionLegend
				let actionName = sections[i].name
				let currentAction = null
				let combos = null
				let gtoRange = null

				if (
					isViewer &&
					(viewMode === constants.ONE_PLAYER ||
						(viewMode === constants.GRAPHICS && reportsType !== constants.EQ_GRAPHIC))
				) {
					actionName = sizingsStructureData[activeCard]?.[sections[i].name]
					currentAction = action_struct.find((el) => el?.action === sections[i].name)

					currentAction = {
						...currentAction,
						values: filteredValues(currentAction, combosFilter, drawsFilter, suits, threshold),
					}

					combos = calculateCombos(currentAction)
					gtoRange = calculateGtoRange(currentAction, abs)
				}

				if (activeIndexArray && activeIndexArray.length > 0 && activeIndexArray.indexOf(i) === -1) {
					isActive = false
				}

				actionLegendStyle = isBlockLayout ? stylesBlock.actionLegendActive : styles.actionLegendActive

				const fullName = sections[i].isAllIn ? actionName + ' (All in)' : actionName
				gtoRange = (gtoRange / abs) * 100

				const percent = isAggFLop ? (averageFlopValue[sections[i].id] * 100).toFixed(2) : gtoRange
				const actionPercent = isNaN(percent) ? 0.0 : percent

				legendData.push({
					isActive,
					actionLegendStyle,
					id: sections[i].id,
					color: sections[i].color,
					name: fullName,
					gtoRange: Number(actionPercent).toFixed(2),
					width: actionPercent,
					combos: Math.round(combos * 100) / 100,
				})
			}
		}

		return legendData
	}, [
		sections,
		action_struct,
		combosFilter,
		drawsFilter,
		structureWithSameCountOfAction,
		activeIndexArray,
		viewMode,
		isViewer,
		isUnionMultiReport,
		averageFlopValue,
	])

	const renderCircles = useMemo(
		() => (
			<Row
				className={styles.actionLegendRow}
				style={{ ...style, justifyContent: isCenterLayout ? 'center' : 'flex-start' }}
			>
				{renderSections.map((action, i) =>
					withOverlay(
						<span
							className={action.actionLegendStyle}
							key={action.name}
							onClick={
								onSectionClicked
									? (e) => {
											const index = isAggFLop ? i : action.id

											onSectionClicked(index, e)
									  }
									: null
							}
						>
							<Badge color={action.color} text={action.name} />
						</span>,
						checkParentActions
					)
				)}
			</Row>
		),
		[renderSections]
	)

	const resetActionFilter = () => {
		// if (isAggRunouts && sortedAction.length > 0) {
		// 	dispatch(setStrategyFilters({ filterType: 'activeActions', filterData: sortedAction }))
		// } else {
		dispatch(setStrategyFilters({ filterType: 'activeActions', filterData: [] }))
		// }
	}

	const renderBlocks = useMemo(() => {
		return (
			<Row className={stylesBlock.legendsContainer}>
				{activeActions.length > 0 && (
					<SecondaryButton
						title='✕'
						handleClick={resetActionFilter}
						size={'small'}
						style={{ position: 'absolute', height: 'calc(100% - 10px)', left: '-35px' }}
					/>
				)}

				<Row
					className={stylesBlock.actionLegendRow}
					style={{ ...style, '--custom-border-color': `${customBorderColor}`, textShadow }}
				>
					{renderSections.map((action, i, array) => {
						const style = {
							width: 100 / actionLength + '%',
							backgroundColor: action.color,
							outline: `1px solid ${isHover[action.name] ? 'var(--custom-border-color)' : 'transparent'}`,
							outlineOffset: '-1px',
							color: `rgb(${rgbString(fontColor, 1)})`,
							cursor: isHover[action.name] ? 'pointer' : 'default',
							opacity: action.isActive ? 1.0 : 0.4,
						}

						return (
							<div
								className={classNames(
									action.actionLegendStyle,
									i === 0 ? stylesBlock.roundedLeftCell : '',
									i === array.length - 1 ? stylesBlock.roundedRightCell : ''
								)}
								key={action.name}
								onClick={
									onSectionClicked
										? (e) => {
												const index = isAggFLop ? i : action.id
												onSectionClicked(index, e)
										  }
										: null
								}
								style={style}
								onMouseEnter={() =>
									setIsHover((prevstate) => ({
										...prevstate,
										[action.name]: true,
									}))
								}
								onMouseLeave={() =>
									setIsHover((prevstate) => ({
										...prevstate,
										[action.name]: false,
									}))
								}
							>
								<div className={stylesBlock.actionLegendContainer}>
									<span>{action.name}</span>
								</div>

								<div className={stylesBlock.comboLegendContainer}>
									<span>{action.gtoRange}%</span>

									<span>
										<span className={stylesBlock.comboLabel}>
											{isAggFLop ? '' : `${combosLocale}: ${action.combos}`}
										</span>
									</span>
								</div>
							</div>
						)
					})}
				</Row>

				<Row className={stylesBlock.actionsRow}>
					{renderSections.map((action, i, array) => (
						<div
							key={i}
							style={{ width: action.width + '%', backgroundColor: action.color }}
							className={classNames(
								stylesBlock.actionLinePart,
								i === 0 ? stylesBlock.roundedLeftCell : '',
								i === array.length - 1 ? stylesBlock.roundedRightCell : ''
							)}
						/>
					))}
				</Row>
			</Row>
		)
	}, [renderSections, isHover])

	return isBlockLayout ? renderBlocks : renderCircles
}
